<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Justify self
      </h1>
      <p class="page-title__sub">
        Utility classes for justifying grid items along its inline axis.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.justify-self-center</code>
              </td>
              <td>
                <code class="code__prop">justify-self: center;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.justify-self-end</code>
              </td>
              <td>
                <code class="code__prop">justify-self: flex-end;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.justify-self-start</code>
              </td>
              <td>
                <code class="code__prop">justify-self: flex-start;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.justify-self-stretch</code>
              </td>
              <td>
                <code class="code__prop">justify-self: stretch;</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Center
        </h2>
      </header>
      <p>
        Use <code>.justify-self-center</code> to center grid item along it's inline axis.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid grid-cols-3..."&gt;
  &lt;span class="item col-span-1...">&lt;span>
  &lt;span class="item col-span-1 justify-self-center...">&lt;span>
  &lt;span class="item cols-span-1...">&lt;span>
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview class="preview--padding-10">
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 align-items-start width-full">
            <span class="col-span-1 align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow text-center"
            />
            <span class="col-span-1 justify-self-center padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-7 text-center"
            >
              1
            </span>
            <span class="col-span-1 align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-4 text-center"
            />
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          End
        </h2>
      </header>
      <p>
        Use <code>.justify-self-end</code> to align grid item at the end of it's inline axis.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid grid-cols-3..."&gt;
  &lt;span class="item col-span-1...">&lt;span>
  &lt;span class="item col-span-1 justify-self-end...">&lt;span>
  &lt;span class="item cols-span-1...">&lt;span>
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview class="preview--padding-10">
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 align-items-start width-full">
            <span class="col-span-1 align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow text-center"
            />
            <span class="col-span-1 justify-self-end padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-7 text-center"
            >
              1
            </span>
            <span class="col-span-1 align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-4 text-center"
            />
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Start
        </h2>
      </header>
      <p>
        Use <code>.justify-self-start</code> to align grid item at the start of it's inline axis.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid grid-cols-3..."&gt;
  &lt;span class="item col-span-1...">&lt;span>
  &lt;span class="item col-span-1 justify-self-start...">&lt;span>
  &lt;span class="item cols-span-1...">&lt;span>
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview class="preview--padding-10">
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 align-items-start width-full">
            <span class="col-span-1 align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow text-center"
            />
            <span class="col-span-1 justify-self-start padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-7 text-center"
            >
              1
            </span>
            <span class="col-span-1 align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-4 text-center"
            />
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Stretch
        </h2>
      </header>
      <p>
        Use <code>.justify-self-stretch</code> to stretch grid item to fill he grid area.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid grid-cols-3..."&gt;
  &lt;span class="item col-span-1...">&lt;span>
  &lt;span class="item col-span-1 justify-self-stretch...">&lt;span>
  &lt;span class="item cols-span-1...">&lt;span>
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview class="preview--padding-10">
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 align-items-start width-full">
            <span class="col-span-1 justify-self-start align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow text-center"
            />
            <span class="col-span-1 justify-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-7 text-center"
            >
              1
            </span>
            <span class="col-span-1 justify-self-end align-self-stretch padding-13 padding-left-20 padding-right-20
            margin-10 bg-selective-yellow-4 text-center"
            />
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'JustifySelf',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>
